import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Input, message, Select, Spin } from "antd";
import { Calendar, Copy, Edit2, MapPin, Ruler, Save, Settings, X } from "lucide-react";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useCancelableDebouncedRequest } from "../../../../hooks/useCancelableDebouncedRequest ";
import DeleteDialog from "../../../../shared/components/delete-dialog/DeleteDialog";
import { Store } from "../../../../store";
import { MAX_CAPACITY_OPTIONS } from "../../../../utils/constants";
import { pixelsToUnits, transformElement } from "../../../../utils/helpers";
import { deleteLayoutObjectApi, duplicateLayoutObjectApi, updateLayoutObjectApi } from "../../services/layoutObjectService";
import BinsTab from "../BinsTab/BinsTab";
import "./ObjectInfoPanel.css";

const MIN_RACK_WIDTH = 50;
const MIN_RACK_LENGTH = 50;

const ObjectInfoPanel = React.memo(
  ({
    objects,
    setObjects,
    selectedObject,
    setSelectedObject,
    setShowObjectInfoPanel,
    scaleFactor = 0.01,
    setShowBinConfigModal,
    showBinDirectly,
    setShowBinDirectly,
    showActions,
  }) => {
    const { state, dispatch } = useContext(Store);
    const [activeTab, setActiveTab] = useState("details");
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedData, setEditedData] = useState(null);
    const [isApiCalling, setIsApiCalling] = useState(false);
    const [hasDelete, setHasDelete] = useState(false);
    const saveChangesToDB = useCancelableDebouncedRequest(250);
    // store original object so that when the edit is cancelled, we can revert to the original object
    const [originalObject, setOriginalObject] = useState(selectedObject);

    // Replace the existing useMemo hook for object with this:
    const object = useMemo(() => {
      if (!state.selectedElement) return null;

      // If showBinDirectly is true and has data, we need to find and show the specific bin/subarea
      if (showBinDirectly && showBinDirectly.id) {
        // First, find the parent object that contains this bin/subarea
        // This assumes showBinDirectly contains the full bin/subarea object with parent info
        const parentObjectId = showBinDirectly.objectType === "bin" ? showBinDirectly.rackId : showBinDirectly.areaId;

        if (parentObjectId) {
          // Find the parent object in your objects array or store
          const parentObject = objects.find((obj) => obj.id === parentObjectId) || state.elements?.find((el) => el.id === parentObjectId);

          if (parentObject) {
            // Set the parent as selected object
            setSelectedObject(parentObject);

            // Set the correct tab based on object type
            if (parentObject.objectType === "rack") {
              setActiveTab("bins");
              // Find the specific bin in the parent's bins array
              const specificBin = parentObject.bins?.find((bin) => bin.id === showBinDirectly.id);
              // if (specificBin) {
              //   // You might want to store this in a separate state to highlight the specific bin
              //   // For example, you could set it in context or local state
              //   dispatch({
              //     type: "SET_SELECTED_SUBELEMENT",
              //     payload: { type: "bin", data: specificBin },
              //   });
              // }
            } else if (parentObject.objectType === "area") {
              setActiveTab("subareas");
              // Find the specific subarea in the parent's subAreas array
              const specificSubarea = parentObject.subAreas?.find((sub) => sub.id === showBinDirectly.id);
              // if (specificSubarea) {
              //   dispatch({
              //     type: "SET_SELECTED_SUBELEMENT",
              //     payload: { type: "subarea", data: specificSubarea },
              //   });
              // }
            }

            setShowObjectInfoPanel(true);
            // setOriginalObject(parentObject);
            return parentObject;
          }
        }
      }

      // Otherwise return the currently selected element
      // setOriginalObject(state.selectedElement);
      return state.selectedElement;
    }, [state.selectedElement, showBinDirectly, objects, state.elements]);

    // Memoized formatted date function
    const formatDate = useCallback((dateString) => {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }, []);

    // Memoized object icon function
    const getObjectIcon = useCallback((type) => {
      // change the above to object mapping
      const objectIconMapping = {
        rack: "📦",
        area: "📍",
        dock: "🚪",
        wall: "🧱",
        zone: "🏷️",
        default: "📋",
      };
      return objectIconMapping[type] || objectIconMapping.default;
    }, []);

    // Update editedData when object changes and not in edit mode
    useEffect(() => {
      if (!isEditMode && object) {
        setEditedData({ ...object });
      }
    }, [object, isEditMode]);

    useEffect(() => {
      if (!showBinDirectly) setActiveTab("details");
    }, [object?.id]); // important: object.id must change

    // Early return if no object
    if (!object) return null;

    const currentData = isEditMode ? editedData : object;

    // Edit handler
    const handleEdit = useCallback(() => {
      setIsEditMode(true);
      const snapshot = { ...object };
      setEditedData(snapshot);
      setOriginalObject(snapshot);
    }, [object]);

    // Cancel handler
    const handleCancel = () => {
      setIsEditMode(false);
      setEditedData({ ...originalObject });

      // Revert to original object in all states
      dispatch({
        type: "UPDATE_ELEMENT",
        payload: originalObject, // Original object
      });

      // Update local objects array
      const revertedObjects = objects.map((obj) => (obj.id === originalObject.id ? originalObject : obj));

      setObjects(revertedObjects);

      // Update selectedElementRef
      if (selectedObject && selectedObject.id === originalObject.id) {
        setSelectedObject(originalObject);
      }
    };

    const handleDelete = async (value) => {
      if (value === "cancel") {
        setHasDelete(false);
        return;
      }

      try {
        const data = await saveChangesToDB(deleteLayoutObjectApi, selectedObject.id);
        dispatch({ type: "REMOVE_ELEMENT", payload: selectedObject });
        setHasDelete(false);
        setSelectedObject(null);
        setShowObjectInfoPanel(false);
        message.success("Deleted Successfully");
      } catch (error) {
        console.log(error);
        message.error("Delete failed");
      }
    };

    const handleSave = async () => {
      setIsApiCalling(true);
      // Get the CURRENT object from store (with all live preview updates)
      const currentObjectInStore = state.elements?.find((el) => el.id === object.id) || object;

      // Define which fields the API accepts
      const updatableFields = [
        "name",
        "code",
        "color",
        "length",
        "width",
        "height",
        "maxCapacity",
        "orientation",
        "facingDirection",
        "unitType",
        "positionX",
        "positionY",
      ];

      // Compare current store object with original object to find changed fields
      const changedData = {};
      updatableFields.forEach((field) => {
        const currentValue = currentObjectInStore[field];
        const originalValue = originalObject?.[field];

        // Handle number comparisons
        if (typeof originalValue === "number" && typeof currentValue === "number") {
          if (currentValue !== originalValue) {
            changedData[field] = currentValue;
          }
        }
        // Handle string/other comparisons
        else if (currentValue !== originalValue) {
          changedData[field] = currentValue;
        }

        // if the changings are length, width, positionX, positionY then convert it into pixels
        if (changedData[field] !== undefined && (field === "length" || field === "width" || field === "positionX" || field === "positionY")) {
          changedData[field] = pixelsToUnits(currentValue, object.unitType);
        }
      });

      // Only proceed if there are actual changes
      if (Object.keys(changedData).length === 0) {
        setIsEditMode(false);
        return;
      }

      const payload = {
        warehouseLayoutId: currentObjectInStore.warehouseLayoutId,
        layoutObject: {
          ...changedData,
          maxCapacity: Number(currentObjectInStore.maxCapacity),
        }, // Only send changed fields
      };

      try {
        // Save to DB
        await saveChangesToDB(updateLayoutObjectApi, {
          id: currentObjectInStore.id,
          payload,
        });

        // Mark as saved - create updated object with all current data
        let updatedObject = {
          ...currentObjectInStore,
          hasElementChanges: true,
          updatedAt: new Date().toISOString(),
        };

        // Frontend Cascade: if maxCapacity changed, update all children
        if (changedData.maxCapacity !== undefined) {
          const newMaxCap = Number(changedData.maxCapacity);
          if (updatedObject.objectType === "rack" && updatedObject.bins) {
            updatedObject.bins = updatedObject.bins.map((bin) => ({
              ...bin,
              maxCapacity: newMaxCap,
            }));
          } else if (updatedObject.objectType === "area" && updatedObject.subAreas) {
            updatedObject.subAreas = updatedObject.subAreas.map((sa) => ({
              ...sa,
              maxCapacity: newMaxCap,
            }));
          }
        }

        console.log("updatedObject after cascade", updatedObject);
        // Update global store
        dispatch({
          type: "UPDATE_ELEMENT",
          payload: updatedObject,
        });

        // Update local objects array
        const updatedObjects = objects.map((obj) => {
          if (obj.id === currentObjectInStore.id) {
            return updatedObject;
          }
          return obj;
        });

        setObjects(updatedObjects);

        // Update the selectedElementRef
        if (selectedObject && selectedObject.id === currentObjectInStore.id) {
          setSelectedObject(updatedObject);
        }

        setIsEditMode(false);
        setEditedData(updatedObject);
      } catch (error) {
        console.error("Save failed", error);

        // On error, revert to original data in the form
        setEditedData({ ...object });

        throw error;
      } finally {
        setIsApiCalling(false);
      }
    };

    const handleInputChange = (field, value) => {
      // Update editedData
      setEditedData((prev) => ({ ...prev, [field]: value }));

      // Update live preview
      if (selectedObject) {
        // Merge original object with accumulated edits (editedData)
        if(field === 'maxCapacity') {
          // check if the number is negative
          if(value < 0) {
            value = 0;
          }
        }
        const obj = { ...selectedObject, ...editedData };

        // Helper to update 3D properties based on 2D changes
        const update3DProps = (o) => {
          const x1 = Number(o.positionX || o.x1 || 0);
          const y1 = Number(o.positionY || o.y1 || 0);
          const w = Number(o.width || o.x2 - o.x1 || 0);
          const l = Number(o.length || o.y2 - o.y1 || 0);
          const h = Number(o.height || 0);

          // Update x1, x2, y1, y2
          o.x1 = x1;
          o.x2 = x1 + w;
          o.y1 = y1;
          o.y2 = y1 + l;

          // Calculate 3D size
          // Note: Logic might vary by object type, but this is a reasonable approximation for live preview
          // For walls, height is Y. For others, it might be different.
          // Using generic box logic:
          o.size = [w * scaleFactor, h * scaleFactor, l * scaleFactor];

          // For Area/Zone, height (Y) is usually 0 or small fixed value.
          if (o.objectType === "area" || o.objectType === "zone") {
            o.size[1] = 0; // Flat
          }

          // Calculate 3D position (Center)
          // X in 2D -> X in 3D
          // Y in 2D -> Z in 3D
          // Y in 3D -> usually 0 or half height
          const centerX = x1 + w / 2;
          const centerZ = y1 + l / 2;

          let centerY = 0;
          if (o.objectType === "dock") {
            o.size[1] = 2 * (scaleFactor * 100); // from Index.jsx logic
          }

          // Adjust Y based on type if needed (e.g. Wall)
          // For now, keep Y as is or 0
          if (o.position && o.position[1]) centerY = o.position[1];

          o.position = [centerX * scaleFactor, centerY, centerZ * scaleFactor];
          return o;
        };

        switch (field) {
          case "positionX":
            obj.positionX = Math.round(Number(value));
            update3DProps(obj);
            break;
          case "positionY":
            obj.positionY = Math.round(Number(value));
            update3DProps(obj);
            break;
          case "length":
            obj.length = Math.round(Number(value));
            update3DProps(obj);
            break;
          case "width":
            obj.width = Math.round(Number(value));
            update3DProps(obj);
            break;
          case "height":
            obj.height = Math.round(Number(value));
            update3DProps(obj);
            break;
          case "orientation":
            obj.orientation = Math.round(Number(value));
            obj.rotation = obj.orientation; // Update rotation as well
            break;
          case "name":
            obj.name = value;
            break;
          case "color":
            obj.color = value;
            break;
          case "zIndex":
            obj.zIndex = Math.round(Number(value));
            break;
          case "facingDirection":
            obj.facingDirection = value;
            break;
          default:
            obj[field] = value;
        }
        // Trigger re-render by dispatching existing UPDATE_ELEMENT
        // This will update the 3D preview
        dispatch({
          type: "UPDATE_ELEMENT",
          payload: obj, // live preview
        });

        // Update the object in the objects array
        const updatedObjects = objects.map((o) => (o.id === object.id ? obj : o));
        setObjects(updatedObjects);
        setShowBinDirectly(null);
      }
    };

    const handleClose = () => {
      setSelectedObject(null);
      setShowObjectInfoPanel(false);
      setOriginalObject(null);
      // reset the edited data
      setEditedData({});
      // update the selected element in the store with the last saved object
      dispatch({ type: "UPDATE_ELEMENT", payload: object });
      dispatch({ type: "SET_SELECTED_ELEMENT", payload: null });
    };

    // on initialization, which listens to the key 'Delete', then call the handleDelete function,
    // and also listen to 'ctril + D' to duplicate the object
    useEffect(() => {
      const handleKeyDown = (event) => {
        if (!showActions) return;
        if (event.key === "Delete") {
          event.preventDefault();
          event.stopPropagation();
          handleDelete("delete");
        }
        if (event.key === "d" && event.ctrlKey) {
          event.preventDefault();
          event.stopPropagation();
          handleDuplicate();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, []);

    // change the edited mode to false whenever the active tab changes
    useEffect(() => {
      setIsEditMode((prev) => {
        if (prev) {
          handleCancel();
          return false;
        }
        return prev;
      });
    }, [activeTab]);

    // Memoized computed values
    const hasBins = useMemo(() => object.objectType.toLowerCase() === "rack", [object.objectType.toLowerCase()]);
    const hasSubAreas = useMemo(() => object.objectType.toLowerCase() === "area", [object.objectType.toLowerCase()]);

    const PanelHeader = () => {
      return (
        <div className="panel-header">
          <div className="header-left">
            <div className="header-icon">{getObjectIcon(object.objectType)}</div>
            <div className="header-text">
              <div className="object-type">
                {object.objectType} #{object.id}
              </div>
              <h2 className="object-name">{object.name}</h2>
            </div>
          </div>
          <div className="header-buttons">
            {/* <button className="icon-button">
            <Eye size={18} className="button-icon" />
          </button> */}
            {showActions && (
              <button
                className="icon-button"
                onClick={() => {
                  setHasDelete(true);
                }}
              >
                <FontAwesomeIcon icon={faTrashCan} title="Delete" className="tool-icon element-delete tool-delete" />
              </button>
            )}
            <button onClick={handleClose} className="icon-button">
              <X size={20} className="button-icon" />
            </button>
          </div>
        </div>
      );
    };

    const handleDuplicate = async () => {
      if (isApiCalling) return; // prevent multiple clicks
      setIsApiCalling(true);
      try {
        const response = await saveChangesToDB(duplicateLayoutObjectApi, object.id);
        const data = response?.data?.data;
        dispatch({ type: "ADD_ELEMENT", payload: transformElement(data) });
      } catch (error) {
        message.error("Failed to duplicate layout object");
        console.log("error", error);
      } finally {
        // exit edit mode if any
        setIsApiCalling(false);
      }
    };

    const showConfigModal = () => {
      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "SET_SELECTED_ELEMENT", payload: object },
          { type: "RE_SET_SELECTED_ELEMENT", payload: object },
          { type: "RE_SET_ELEMENTS", payload: object.objectType === "rack" ? object.bins || [] : object.subAreas || [] },
        ],
      });
      setShowBinConfigModal(true);
      setShowObjectInfoPanel(false);
    };

    return (
      <div className="object-info-panel">
        <div className="panel-container">
          {PanelHeader()}

          <div className="object-info-tabs-container">
            <button onClick={() => setActiveTab("details")} className={`tab-button ${activeTab === "details" ? "active" : ""}`}>
              Details
            </button>
            {hasBins && (
              <button onClick={() => setActiveTab("bins")} className={`tab-button ${activeTab === "bins" ? "active" : ""}`}>
                Bins ({object.bins?.length || 0})
              </button>
            )}
            {hasSubAreas && (
              <button onClick={() => setActiveTab("subareas")} className={`tab-button ${activeTab === "subareas" ? "active" : ""}`}>
                Sub-areas ({object.subAreas?.length || 0})
              </button>
            )}
          </div>

          <div className="panel-body">
            {activeTab === "details" && (
              <div className="details-content">
                <div className="section">
                  <h3 className="section-title">Basic Information</h3>
                  <div className="form-section">
                    <div className="form-row">
                      <span className="row-label">Code</span>
                      <div className="row-content">
                        {isEditMode ? (
                          <input
                            value={currentData.code}
                            onChange={(e) => handleInputChange("code", e.target.value)}
                            className="input-field"
                            style={{ width: "128px" }}
                          />
                        ) : (
                          <span className="formatted-text">{currentData.code}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <span className="row-label">Name</span>
                      <div className="row-content">
                        {isEditMode ? (
                          <input
                            value={currentData.name}
                            onChange={(e) => handleInputChange("name", e.target.value)}
                            className="input-field"
                            style={{ width: "160px" }}
                          />
                        ) : (
                          <span className="formatted-text">{currentData.name}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <span className="row-label">Type</span>
                      <div className="row-content">
                        <span className="formatted-text capitalize-text">{currentData.objectType}</span>
                      </div>
                    </div>

                    {currentData.facingDirection && (
                      <div className="form-row">
                        <span className="row-label">Facing</span>
                        <div className="row-content">
                          {isEditMode ? (
                            <Select
                              value={currentData.facingDirection}
                              onChange={(value) => handleInputChange("facingDirection", value)}
                              style={{ width: 80 }}
                              options={[
                                { label: "EW", value: "EW" },
                                { label: "NS", value: "NS" },
                              ]}
                              className="select-field" // styles the input box
                              classNames={{
                                popup: {
                                  root: "select-dropdown",
                                },
                              }}
                            />
                          ) : (
                            <span className="formatted-text">{currentData.facingDirection}</span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="form-row">
                      <span className="row-label">Color</span>
                      <div className="row-content">
                        {isEditMode ? (
                          <div className="color-input-group">
                            <input type="color" value={currentData.color} onChange={(e) => handleInputChange("color", e.target.value)} />
                            <input
                              value={currentData.color}
                              onChange={(e) => handleInputChange("color", e.target.value)}
                              className="input-field color-text-input"
                            />
                          </div>
                        ) : (
                          <div className="color-input-group">
                            <div className="color-preview" style={{ backgroundColor: currentData.color }} />
                            <span className="formatted-text">{currentData.color}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* form-row for viewing and editing max capacity */}
                    {(object.objectType.toLowerCase() === "rack" || object.objectType.toLowerCase() === "area") && (
                      <div className="form-row">
                        <span className="row-label">Max Capacity</span>
                        <div className="row-content">
                          {isEditMode ? (
                            <Input
                              type="number"
                              min={0}
                              value={currentData.maxCapacity}
                              onChange={(e) => handleInputChange("maxCapacity", e.target.value)}
                              className="input-field"
                              maxLength={4}
                            />
                          ) : (
                            <span className="formatted-text">{currentData.maxCapacity || "N/A"}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">
                      <Ruler size={16} className="button-icon" /> Dimensions
                    </h3>
                  </div>
                  <div className="form-section">
                    <div className="form-row">
                      <span className="row-label">Length</span>
                      <div className="row-content">
                        {isEditMode && currentData.subAreas?.length === 0 ? (
                          <div className="field-group">
                            <input
                              type="number"
                              value={currentData.length}
                              onChange={(e) => handleInputChange("length", e.target.value)}
                              className="input-field number-input"
                              min={MIN_RACK_LENGTH}
                            />
                            <span className="unit-label">{currentData.unitType}</span>
                          </div>
                        ) : (
                          <span className="formatted-text">
                            {currentData.length} {currentData.unitType}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <span className="row-label">Width</span>
                      <div className="row-content">
                        {isEditMode && currentData.subAreas?.length === 0 ? (
                          <div className="field-group">
                            <input
                              type="number"
                              value={currentData.width}
                              onChange={(e) => handleInputChange("width", e.target.value)}
                              className="input-field number-input"
                              min={MIN_RACK_WIDTH}
                            />
                            <span className="unit-label">{currentData.unitType}</span>
                          </div>
                        ) : (
                          <span className="formatted-text">
                            {currentData.width} {currentData.unitType}
                          </span>
                        )}
                      </div>
                    </div>

                    {(currentData.height || (isEditMode && currentData.objectType === "wall")) && (
                      <div className="form-row">
                        <span className="row-label">Height</span>
                        <div className="row-content">
                          {isEditMode && currentData.subAreas?.length === 0 ? (
                            <div className="field-group">
                              <input
                                type="number"
                                value={currentData.height || ""}
                                onChange={(e) => handleInputChange("height", e.target.value)}
                                className="input-field number-input"
                              />
                              <span className="unit-label">{currentData.unitType}</span>
                            </div>
                          ) : (
                            <span className="formatted-text">
                              {currentData.height} {currentData.unitType}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="section">
                  <h3 className="section-title">
                    <MapPin size={16} className="button-icon" /> Position
                  </h3>
                  <div className="form-section">
                    <div className="form-row">
                      <span className="row-label">X</span>
                      <div className="row-content">
                        {isEditMode && currentData.subAreas?.length === 0 ? (
                          <input
                            type="number"
                            value={currentData.positionX}
                            onChange={(e) => handleInputChange("positionX", e.target.value)}
                            className="input-field"
                            style={{ width: "128px" }}
                          />
                        ) : (
                          <span className="formatted-text">{currentData.positionX}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <span className="row-label">Y</span>
                      <div className="row-content">
                        {isEditMode && currentData.subAreas?.length === 0 ? (
                          <input
                            type="number"
                            value={currentData.positionY}
                            onChange={(e) => handleInputChange("positionY", e.target.value)}
                            className="input-field"
                            style={{ width: "128px" }}
                          />
                        ) : (
                          <span className="formatted-text">{currentData.positionY}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <span className="row-label">Orientation</span>
                      <div className="row-content">
                        {isEditMode && currentData.subAreas?.length === 0 ? (
                          <input
                            type="number"
                            value={currentData.orientation || 0}
                            onChange={(e) => handleInputChange("orientation", e.target.value)}
                            className="input-field"
                            style={{ width: "80px" }}
                          />
                        ) : (
                          <span className="formatted-text">{currentData.orientation || 0}°</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <span className="row-label">Z-Index</span>
                      <div className="row-content">
                        {isEditMode && currentData.subAreas?.length === 0 ? (
                          <input
                            type="number"
                            value={currentData.zIndex || 0}
                            onChange={(e) => handleInputChange("zIndex", e.target.value)}
                            className="input-field"
                            style={{ width: "80px" }}
                          />
                        ) : (
                          <span className="formatted-text">{currentData.zIndex}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="section">
                  <h3 className="section-title">
                    <Calendar size={16} className="button-icon" /> Metadata
                  </h3>
                  <div className="metadata-section">
                    <div className="metadata-row">
                      <span className="row-label">Created</span>
                      <div className="row-content">
                        <span className="formatted-text">{formatDate(currentData.createdAt)}</span>
                      </div>
                    </div>
                    <div className="metadata-row">
                      <span className="row-label">Updated</span>
                      <div className="row-content">
                        <span className="formatted-text">{formatDate(currentData.updatedAt)}</span>
                      </div>
                    </div>
                    <div className="metadata-row">
                      <span className="row-label">Layout ID</span>
                      <div className="row-content">
                        <span className="formatted-text">{currentData.warehouseLayoutId}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "bins" && (
              <BinsTab bins={object.bins} onCreateBin={() => showConfigModal()} type="bins" showBinDirectly={showBinDirectly} showActions={showActions} />
            )}
            {activeTab === "subareas" && (
              <BinsTab
                bins={object.subAreas}
                onCreateBin={() => showConfigModal()}
                type="subareas"
                showBinDirectly={showBinDirectly}
                showActions={showActions}
              />
            )}
          </div>

          {showActions ? (
            <div className="panel-footer">
              {isEditMode && (
                <>
                  <button onClick={handleCancel} className="footer-button">
                    Cancel
                  </button>
                  <button onClick={handleSave} className="footer-button primary" disabled={isApiCalling}>
                    {isApiCalling ? (
                      <>
                        <Spin size="small" style={{ marginRight: 8 }} />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} className="button-icon" /> Save Changes
                      </>
                    )}
                  </button>
                </>
              )}
              {!isEditMode && activeTab === "details" && (
                <>
                  <button onClick={handleDuplicate} className="footer-button">
                    {isApiCalling ? (
                      <>
                        <Spin size="small" style={{ marginRight: 8 }} />
                        Duplicating...
                      </>
                    ) : (
                      <>
                        <Copy size={16} className="button-icon" /> Duplicate
                      </>
                    )}
                  </button>
                  <button onClick={handleEdit} className="footer-button primary">
                    <Edit2 size={16} className="button-icon" /> Edit
                  </button>
                </>
              )}
              {!isEditMode && (activeTab === "bins" || activeTab === "subareas") && (
                <button onClick={() => showConfigModal()} className="footer-button">
                  <Settings size={16} className="button-icon" /> Configure {activeTab === "bins" ? "Bins" : "Subareas"}
                </button>
              )}
            </div>
          ) : (
            <div className="panel-footer">
              <button onClick={handleClose} className="footer-button">
                Close
              </button>
            </div>
          )}
        </div>
        {hasDelete && <DeleteDialog recordToDelete={object?.name} dialogActionHandler={handleDelete} />}
      </div>
    );
  },
);

export default ObjectInfoPanel;
